import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Collapse, Descriptions, Flex, Image, Input, Modal, Radio, Segmented, Space, Typography } from 'antd';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useApi } from '../../app/api-context';
import { won, type GenBatch, type GenConfig, type PromptParts, type PromptPreview, type PromptSegment } from '../../lib/generation-types';

export interface ManualSource {
  vocabId: string;
  text: string;
  /** 이 이미지에서 다시 만들기(이전 편집 영역·참고 이미지) */
  asset?: { assetId: string; prompt: string | null; promptParts?: PromptParts | null; modelId: string | null; previewUrl: string | null; hasOriginal: boolean; /** 참고 이미지 첨부를 기본으로(기존 앱 이미지에서 새로 만들기, #76) */ preferReference?: boolean } | null;
}

export const SUBJECT_MAX = 200;
export const EXTRA_MAX = 1000;

/** 입력이 잠시 멈춘 뒤의 값(미리보기 요청을 줄인다) */
function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

type Lang = 'ko' | 'en';

function Segments({ items, lang }: { items: PromptSegment[]; lang: Lang }) {
  return (
    <>
      {items.map((s, i) => (
        <Typography.Paragraph key={i} style={{ marginBottom: 6, fontSize: 13 }}>
          {lang === 'ko' ? s.ko : s.en}
        </Typography.Paragraph>
      ))}
    </>
  );
}

/**
 * 수동 제작(R2 §3.2). 편집 영역(피사체 설명·추가 지시)만 한글로 고치고, 공통 규칙(단일 객체·만 5세 기준·구도·배경)은 서버가 붙인다.
 * 모델에 보내는 영문 프롬프트를 문장마다 한국어 번역과 함께 보여준다. 결과는 자동 제작과 같은 후처리·검수·게시 흐름.
 */
export function ManualModal({ source, onClose }: { source: ManualSource | null; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const open = !!source;
  const config = useQuery({ queryKey: ['generation', 'config'], queryFn: () => api.get<GenConfig>('/generation/config'), enabled: open, retry: false });
  const [modelId, setModelId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState(1);
  const [subject, setSubject] = useState('');
  const [extra, setExtra] = useState('');
  const [ready, setReady] = useState(false);
  const [useRef, setUseRef] = useState(false);
  const [lang, setLang] = useState<Lang>('ko');

  const model = modelId ?? source?.asset?.modelId ?? config.data?.models[0]?.id ?? null;
  const base = useQuery({
    queryKey: ['generation', 'prompt', source?.vocabId, model],
    queryFn: () => api.get<PromptPreview>(`/generation/prompt?${new URLSearchParams({ vocabId: source!.vocabId, modelId: model! })}`),
    enabled: open && !!model,
    retry: false,
  });

  // 열 때마다 초기화: 이전 이미지의 편집 영역, 없으면 어휘 기본값(생성 피사체 설명)
  useEffect(() => {
    if (!source) return;
    setModelId(null);
    setCandidates(1);
    setUseRef(!!source.asset?.preferReference && !!source.asset.hasOriginal);
    const p = source.asset?.promptParts;
    setSubject(p?.subject ?? '');
    setExtra(p?.extra ?? '');
    setReady(!!p);
  }, [source]);
  useEffect(() => {
    if (open && !ready && base.data) {
      setSubject(base.data.defaults.subject ?? '');
      setExtra(base.data.defaults.extra ?? '');
      setReady(true);
    }
  }, [open, ready, base.data]);

  const quoteError = subject.includes('"');
  const dSubject = useDebounced(subject);
  const dExtra = useDebounced(extra);
  const preview = useQuery({
    queryKey: ['generation', 'prompt', source?.vocabId, model, dSubject, dExtra],
    queryFn: () => api.get<PromptPreview>(`/generation/prompt?${new URLSearchParams({ vocabId: source!.vocabId, modelId: model!, subject: dSubject, extra: dExtra })}`),
    enabled: open && !!model && ready && !dSubject.includes('"'),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const m = config.data?.models.find((x) => x.id === model);
  const estimate = m ? m.reserveKrw * candidates * (useRef ? 2 : 1) : null;
  const b = config.data?.budget;
  const remaining = b ? b.limitKrw - b.spentKrw - b.reservedKrw : null;
  const parts: PromptParts = { subject: subject.trim() || null, extra: extra.trim() || null };
  const create = useMutation({
    mutationFn: () =>
      api.post<{ batch: GenBatch }>('/generation/manual', {
        vocabId: source!.vocabId,
        parts,
        modelId: model,
        candidates,
        parentAssetId: source!.asset?.assetId ?? null,
        referenceAssetId: useRef ? source!.asset?.assetId ?? null : null,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['generation'] });
      onClose();
      navigate(`/images?batch=${encodeURIComponent(r.batch.batchId)}`);
    },
  });
  const partsOk = ready && !quoteError && subject.length <= SUBJECT_MAX && extra.length <= EXTRA_MAX;
  // 이전 이미지가 편집 영역 없이 만들어졌으면(이전 방식) 그 프롬프트를 참고로만 보여준다
  const legacyPrompt = source?.asset && !source.asset.promptParts ? source.asset.prompt : null;
  const shown = preview.data;

  return (
    <Modal
      open={open}
      width={760}
      title={source ? `수동 제작 · ${source.text}` : ''}
      okText={`${candidates}장 생성 요청`}
      cancelText="취소"
      onCancel={onClose}
      onOk={() => create.mutate()}
      okButtonProps={{ disabled: !config.data?.configured || !partsOk || !model || (remaining != null && estimate != null && estimate > remaining) }}
      confirmLoading={create.isPending}
      destroyOnHidden
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {source?.asset?.previewUrl && (
          <Flex gap={12} align="center">
            <Image src={source.asset.previewUrl} width={96} height={96} style={{ objectFit: 'contain', background: '#fafafa' }} alt="이전 이미지" />
            <Typography.Text type="secondary">이 이미지에서 다시 만듭니다. 결과는 이전 이미지와 연결되어 검수함에서 함께 비교할 수 있습니다.</Typography.Text>
          </Flex>
        )}
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="모델">
            <Radio.Group value={model} onChange={(e) => setModelId(e.target.value)}>
              <Space direction="vertical">
                {config.data?.models.map((x) => <Radio key={x.id} value={x.id}>{x.label} <Typography.Text type="secondary">(장당 최대 {won(x.reserveKrw)})</Typography.Text></Radio>)}
              </Space>
            </Radio.Group>
          </Descriptions.Item>
          <Descriptions.Item label="후보">
            <Radio.Group value={candidates} onChange={(e) => setCandidates(e.target.value)} options={[1, 2, 3].map((n) => ({ value: n, label: `${n}장` }))} />
          </Descriptions.Item>
          {source?.asset?.hasOriginal && (
            <Descriptions.Item label="참고 이미지">
              <Checkbox checked={useRef} onChange={(e) => setUseRef(e.target.checked)}>이전 이미지를 참고 이미지로 첨부(구도·형태 유지, 예약 비용 2배)</Checkbox>
            </Descriptions.Item>
          )}
        </Descriptions>

        <div data-testid="prompt-edit" style={{ border: '1px solid #91caff', background: '#f0f7ff', borderRadius: 8, padding: 12 }}>
          <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
            <Typography.Text strong>편집 영역 (한글로 입력)</Typography.Text>
            <Button size="small" disabled={!base.data} onClick={() => { if (base.data) { setSubject(base.data.defaults.subject ?? ''); setExtra(base.data.defaults.extra ?? ''); } }}>
              어휘 기본값으로
            </Button>
          </Flex>
          <Typography.Text style={{ fontSize: 13 }}>피사체 설명</Typography.Text>
          <Input aria-label="피사체 설명" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={SUBJECT_MAX} showCount status={quoteError ? 'error' : undefined} placeholder="예: 만 5세 아이의 한쪽 눈 클로즈업(얼굴 전체 아님)" />
          <Typography.Text type={quoteError ? 'danger' : 'secondary'} style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {quoteError ? '큰따옴표(")는 쓸 수 없습니다.' : '그릴 것 하나를 정확히 적어 주세요. 비우면 어휘 이름과 뜻 힌트(슬러그·키워드)로 만듭니다.'}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 13 }}>추가 지시 (선택)</Typography.Text>
          <Input.TextArea aria-label="추가 지시" value={extra} onChange={(e) => setExtra(e.target.value)} maxLength={EXTRA_MAX} autoSize={{ minRows: 2, maxRows: 6 }} placeholder="예: 정면에서, 눈썹까지만 보이게" />
        </div>

        <div data-testid="prompt-preview">
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Typography.Text strong>모델에 보내는 프롬프트</Typography.Text>
            <Segmented<Lang> size="small" value={lang} onChange={setLang} options={[{ value: 'ko', label: '한국어 번역' }, { value: 'en', label: '영문 원문' }]} />
          </Flex>
          {preview.isError && <Alert type="error" showIcon message="프롬프트를 만들지 못했습니다" description={(preview.error as Error).message} />}
          {shown && (
            <>
              <div style={{ borderInlineStart: '3px solid #1677ff', paddingInlineStart: 10, marginBottom: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>편집 영역으로 만든 문장</Typography.Text>
                <Segments items={[shown.subject]} lang={lang} />
              </div>
              <div style={{ borderInlineStart: '3px solid #d9d9d9', paddingInlineStart: 10, color: '#595959' }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>공통 규칙 (모든 이미지에 자동 적용, 수정 불가)</Typography.Text>
                <Segments items={shown.common} lang={lang} />
              </div>
            </>
          )}
          {lang === 'ko' && <Typography.Text type="secondary" style={{ fontSize: 12 }}>번역은 확인용입니다. 모델에는 영문 원문을 보냅니다.</Typography.Text>}
        </div>

        {legacyPrompt && (
          <Collapse
            size="small"
            items={[{ key: 'legacy', label: '이전 이미지의 프롬프트(이전 방식, 참고용)', children: <Typography.Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginBottom: 0 }}>{legacyPrompt}</Typography.Paragraph> }]}
          />
        )}
        {estimate != null && b && (
          <Alert
            type={remaining != null && estimate > remaining ? 'error' : 'info'}
            showIcon
            message={`예상 최대 ${won(estimate)}`}
            description={`이번 달 사용 ${won(b.spentKrw)} · 예약 ${won(b.reservedKrw)} · 남은 한도 ${won(remaining ?? 0)}. 실제 비용으로 정산됩니다.`}
          />
        )}
        {config.data && !config.data.configured && <Alert type="warning" showIcon message="이미지 생성 자격증명이 설정되지 않았습니다." />}
        {create.isError && <Alert type="error" showIcon message="요청하지 못했습니다" description={(create.error as Error).message} />}
      </Space>
    </Modal>
  );
}
